'use client';

import { useState } from 'react';

interface ContactFormProps {
  onFormSubmit?: () => void;
}

export default function ContactForm({ onFormSubmit }: ContactFormProps) {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    telegram: '',
    company: '',
    agree: false
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    // Сбрасываем ошибку при изменении поля
    if (error) setError(null);
  };

  const isFormValid = 
    formData.firstName.trim() !== '' &&
    formData.lastName.trim() !== '' &&
    formData.email.trim() !== '' &&
    formData.company.trim() !== '' &&
    formData.agree;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isFormValid || isLoading) return;

    setIsLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const response = await fetch('/api/send-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Ошибка отправки формы');
      }

      // Успешная отправка
      setSuccess(true);
      
      // Очистка формы
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        telegram: '',
        company: '',
        agree: false
      });

      // Вызов callback
      if (onFormSubmit) {
        onFormSubmit();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Произошла ошибка при отправке формы');
      console.error('Form submission error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form className="flex flex-col gap-[13px]" onSubmit={handleSubmit}>
      <div className="flex flex-col sm:flex-row gap-[20px]">
        <div className="flex-1 flex flex-col gap-[8px]">
          <label className="font-manrope font-normal text-[15px] text-[#111827]">
            First name*
          </label>
          <input
            type="text"
            name="firstName"
            value={formData.firstName}
            onChange={handleChange}
            className="bg-white border border-[#eee] rounded-[12px] h-[52px] px-[10px] font-manrope font-normal text-[14px] text-[#111827] focus:outline-none focus:border-[#8436ff]"
            placeholder=" "
            required
          />
        </div>
        <div className="flex-1 flex flex-col gap-[8px]">
          <label className="font-manrope font-normal text-[15px] text-[#111827]">
            Last name*
          </label>
          <input
            type="text"
            name="lastName"
            value={formData.lastName}
            onChange={handleChange}
            className="bg-white border border-[#eee] rounded-[12px] h-[52px] px-[10px] font-manrope font-normal text-[14px] text-[#111827] focus:outline-none focus:border-[#8436ff]"
            placeholder=" "
            required
          />
        </div>
      </div>
      
      <div className="flex flex-col gap-[8px]">
        <label className="font-manrope font-normal text-[15px] text-[#111827]">
          Email*
        </label>
        <input
          type="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          className="bg-white border border-[#eee] rounded-[12px] h-[52px] px-[10px] font-manrope font-normal text-[14px] text-[#111827] focus:outline-none focus:border-[#8436ff]"
          placeholder=" "
          required
        />
      </div>
      
      <div className="flex flex-col gap-[8px]">
        <label className="font-manrope font-normal text-[15px] text-[#111827]">
          Telegram ID
        </label>
        <input
          type="text"
          name="telegram"
          value={formData.telegram}
          onChange={handleChange}
          className="bg-white border border-[#eee] rounded-[12px] h-[52px] px-[10px] font-manrope font-normal text-[14px] text-[#111827] focus:outline-none focus:border-[#8436ff]"
          placeholder=" "
        />
      </div>
      
      <div className="flex flex-col gap-[8px]">
        <label className="font-manrope font-normal text-[15px] text-[#111827]">
          Company name*
        </label>
        <input
          type="text"
          name="company"
          value={formData.company}
          onChange={handleChange}
          className="bg-white border border-[#eee] rounded-[12px] h-[52px] px-[10px] font-manrope font-normal text-[14px] text-[#111827] focus:outline-none focus:border-[#8436ff]"
          placeholder=" "
          required
        />
      </div>
      
      <div className="flex gap-[8px] items-center">
        <input 
          type="checkbox" 
          name="agree"
          checked={formData.agree}
          onChange={handleChange}
          className="w-[15px] h-[15px] rounded-[4px] cursor-pointer" 
          style={{
            borderRadius: '4px',
            border: '1px solid #8436FF',
            backgroundColor: formData.agree ? '#8436FF' : 'transparent',
            accentColor: '#8436FF'
          }}
        />
        <label className="font-manrope font-normal text-[12px] text-[#6b7280] uppercase cursor-pointer">
          * I Agree with Terms and Privacy Policy
        </label>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-[12px] px-[15px] py-[10px]">
          <p className="font-manrope font-normal text-[14px] text-red-600">
            {error}
          </p>
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 rounded-[12px] px-[15px] py-[10px]">
          <p className="font-manrope font-normal text-[14px] text-green-600">
            Спасибо! Ваша заявка успешно отправлена.
          </p>
        </div>
      )}
      
      <button
        type="submit"
        className={`px-[35px] py-[15px] rounded-[50px] font-inter font-bold text-[16px] text-white leading-[25px] mt-[10px] transition-colors ${
          isFormValid && !isLoading
            ? 'bg-[#8436ff] hover:opacity-90 cursor-pointer' 
            : 'bg-[#6b7280] cursor-not-allowed'
        }`}
        disabled={!isFormValid || isLoading}
      >
        {isLoading ? 'Submitting...' : 'Send'}
      </button>
    </form>
  );
}
